/**
 * sl-m82f.4.1: Single unified table component used by every feature tab.
 *
 * Replaces the legacy `GroupedTable` + `UnifiedTable` duo — those two did
 * basically the same thing with different rendering, so they are merged
 * into one DRY component driven by props:
 *
 * - `searchable` enables the search input (only shown when `rows.length > 5`)
 * - `countLabel` customises the "Calls" column header (e.g. "Executions")
 * - `emptyLabel` customises the empty-state text
 *
 * Rows come through as normalized `BreakdownRow` objects — each tab
 * converts its REST response into this shape before passing it in, so the
 * table stays dumb.
 */
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { formatCount, formatCredits } from './format';
import { Pagination } from './pagination';
import {
  emptyRow,
  searchInputStyle,
  tableStyle,
  tdRight,
  tdStyle,
  thRight,
  thStyle,
} from './styles';
import type { BreakdownRow } from './types';

export interface UsageTableProps {
  rows: BreakdownRow[];
  nameHeader: string;
  countLabel?: string;
  emptyLabel?: string;
  searchable?: boolean;
  pageSize?: number;
  /**
   * Custom renderer for the name cell. Defaults to the plain `row.name`
   * (+ optional subtext). Doc-keyed tabs pass a renderer that resolves the
   * document title and shows an open-doc button.
   */
  renderName?: (row: BreakdownRow) => ReactNode;
}

const SEARCH_THRESHOLD = 5;
const DEFAULT_PAGE_SIZE = 20;

export const UsageTable = ({
  rows,
  nameHeader,
  countLabel = 'Calls',
  emptyLabel = 'No data for this period',
  searchable = true,
  pageSize = DEFAULT_PAGE_SIZE,
  renderName,
}: UsageTableProps) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const showSearch = searchable && rows.length > SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      row =>
        row.name.toLowerCase().includes(query) ||
        (row.subtext?.toLowerCase().includes(query) ?? false)
    );
  }, [rows, search]);

  // Keep the page inside the valid range as the filter shrinks the row set.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (page >= totalPages) setPage(0);
  }, [filtered.length, pageSize, page]);

  const pageRows = useMemo(
    () => filtered.slice(page * pageSize, (page + 1) * pageSize),
    [filtered, page, pageSize]
  );

  return (
    <div>
      {showSearch && (
        <input
          style={searchInputStyle}
          placeholder={`Search ${nameHeader}…`}
          value={search}
          onChange={event => {
            setSearch(event.target.value);
            setPage(0);
          }}
        />
      )}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>{nameHeader}</th>
            <th style={thRight}>Credits</th>
            <th style={thRight}>{countLabel}</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 && (
            <tr>
              <td colSpan={3} style={emptyRow}>
                {search ? 'No results' : emptyLabel}
              </td>
            </tr>
          )}
          {pageRows.map(row => (
            <tr key={row.id}>
              <td style={tdStyle}>
                {renderName ? (
                  renderName(row)
                ) : (
                  <>
                    {row.name}
                    {row.subtext && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: '11px',
                          color: 'var(--streamlain-text-secondary-color)',
                        }}
                      >
                        {row.subtext}
                      </span>
                    )}
                  </>
                )}
              </td>
              <td style={tdRight}>{formatCredits(row.credits)}</td>
              <td style={tdRight}>{formatCount(row.calls)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={filtered.length}
        onPageChange={setPage}
      />
    </div>
  );
};
